import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { validateAll } from "@frui.ts/validation";
import { action, computed, observable } from "mobx";
import { CountryIdValidationRules, LicensePlateConfirmationValidationRules, LicensePlateValidationRules } from "models/orderItem";
import { CountryIdConfirmationValidationRules } from "models/vehicle";
import { putChargeContactSuccess, putChargeVehicleSuccess } from "services/eventBus";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import ChargeManagementEditViewModel from "./chargeManagementEditViewModel";
import ChargeManagementLicensePlateChangeViewModel from "./chargeManagementLicensePlateChangeViewModel";
import React from "react";
import * as Sentry from "@sentry/react";
import { scrollToFirstError } from "../../../utils/scrollTo";
import VehicleRegistrationResponse from "entities/vehicleRegistrationResponse";
import InputEmail from "../../../entities/InputEmail";
import { TurnstileInstance } from "@marsidev/react-turnstile";

export default class ChargeManagementBeforeValidChargeLicensePlateChangeViewModel extends ChargeManagementLicensePlateChangeViewModel {
  navigationName = "vehicleChangeBeforeValidity";

  @observable turnstileToken: string;

  @computed
  get validationRules() {
    const validationRules = {
      ...CountryIdValidationRules,
      ...CountryIdConfirmationValidationRules,
      ...LicensePlateValidationRules,
      ...LicensePlateConfirmationValidationRules,
    };

    validationRules.countryId = {
      ...validationRules.countryId,
      manualValidation: this.manualValidator,
    };

    validationRules.licensePlate = {
      ...validationRules.licensePlate,
      manualValidation: this.manualValidator,
    };

    return validationRules;
  }

  @computed
  get newVehicle() {
    return this.charge?.newVehicle;
  }

  @computed
  get hasSecondStep() {
    return true;
  }

  get canSubmit() {
    return this.newVehicle?.licensePlate !== undefined && this.newVehicle?.countryId !== undefined;
  }

  protected onInitialize(): Promise<any> | void {
    this.newVehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.newVehicle!, {
      attachValidator: false,
      context: "new",
      countryIDConfirmation: true,
      licensePlateConfirmation: true,
    });
    if (this.charge && !this.charge.emailAddress && this.charge?.notificationPreferences) {
      this.emailInputViewModel = this.emailInputViewModelFactory(
        new InputEmail(this.charge.emailAddress, this.charge.notificationPreferences._emailConfirmation),
        this.translate("emailInputUsageInfo"),
        this.translate("emailInputGDPR", true)
      );
    }

    attachAutomaticBlurValidator(this.newVehicle, this.validationRules);

    return super.onInitialize();
  }

  @bound
  public validate() {
    const objectsForValidation: any[] = [this.charge?.newVehicle];
    if (this.charge && !this.charge.emailAddress) {
      objectsForValidation.push(this.emailInputViewModel?.inputEmail);
    }

    return validateAll(objectsForValidation);
  }

  onTurnstileVerify = async (turnstileToken: string) => {
    if (turnstileToken) {
      this.turnstileViewModel.onTurnstileVerify(turnstileToken);
    }
  };

  @action.bound
  @watchBusy
  async submit(turnstileToken?: string) {
    if (!this.showSecondStep) {
      await this.checkRsvValidity();
      this.showSecondStep = true;
    } else {
      this.sameVehicleCheck();
      this.success = false;
      this.errorMessage = undefined;
      this.secondStepErrorMessage = undefined;
      this.warningMessages = [];

      if (!this.validate()) {
        scrollToFirstError(this.formElement);
        return;
      }

      const newCountryId = this.newVehicle?.countryId;
      const newLicensePlate = this.newVehicle?.licensePlate;

      if (!newCountryId || !newLicensePlate) return;

      if (!turnstileToken || !this.turnstileViewModel.turnstileToken) return;
      const token = turnstileToken || this.turnstileViewModel.turnstileToken;
      this.isLoading = true;

      try {
        if (this.charge && !this.charge.emailAddress) {
          await this.chargeManagementService.editVehicle(
            newCountryId,
            newLicensePlate,
            this.emailInputViewModel?.inputEmail.email,
            token
          );
          return;
        }

        await this.chargeManagementService.editVehicle(newCountryId, newLicensePlate, undefined, token);
      } finally {
        this.isLoading = false;
      }
    }
  }

  @action.bound
  onCancelSecondStep() {
    if (!this.newVehicle) {
      return;
    }
    this.newVehicle.countryId = undefined!;
    this.newVehicle._countryIdConfirmation = undefined!;
    this.newVehicle.licensePlate = undefined!;
    this.newVehicle._licensePlateConfirmation = undefined!;
    this.showSecondStep = false;
    this.hideConfirmChangeButton = false;
    attachAutomaticBlurValidator(this.newVehicle, this.validationRules);
  }

  @bound
  protected async putChargeVehicleSuccess(data: ReturnType<typeof putChargeVehicleSuccess>) {
    this.clearAfterAsyncAction();

    if (!this.charge) return;

    const { countryId, licensePlate, authCode, emailAddress } = data.payload.vehicle;

    this.success = true;
    this.charge.permittedActions.vehicleChangeBeforeValidity.isAllowed = false;
    if (licensePlate) this.charge.vehicle.licensePlate = licensePlate;
    if (countryId) this.charge.vehicle.countryId = countryId;
    if (authCode) this.charge.authCode = authCode;
    if (licensePlate) this.charge.vehicle.licensePlate = licensePlate;
    if (emailAddress) this.charge.emailAddress = emailAddress;
    if (this.charge) {
      this.charge.notificationPreferences._emailConfirmation = emailAddress;
    }
    this.charge.newVehicle = new VehicleRegistrationResponse();

    this.router.navigate(ChargeManagementEditViewModel, undefined, { countryId, licensePlate, authCode });
  }

  // TODO: R-29-2 Remove after user accepted.
  @bound
  protected async putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>) {
    if (data.payload.triggerer !== "licensePlateChange" && data.payload.triggerer !== "contactChange") {
      this.emailInputViewModel = undefined;
      return;
    } else {
      this.emailInputViewModel = this.emailInputViewModelFactory(
        new InputEmail(this.charge?.emailAddress, this.charge?.notificationPreferences._emailConfirmation),
        "",
        ""
      );
    }

    this.clearAfterAsyncAction();

    if (!this.charge) {
      return;
    }
    this.charge._canBeChanged = false;
    this.charge.notificationPreferences.smsNotificationEnabled = data.payload.notificationPreferences?.smsNotificationEnabled;
    this.charge.notificationPreferences.emailNotificationEnabled = data.payload.notificationPreferences?.emailNotificationEnabled;
    this.charge.notificationPreferences._emailAddressNotification =
      data.payload.notificationPreferences?._emailAddressNotification;
    this.charge.notificationPreferences._emailConfirmation = data.payload.emailAddress;
    this.charge.emailAddress = data.payload.emailAddress;
    this.charge.notificationPreferences.phoneNumber = data.payload.notificationPreferences?.phoneNumber;
    if (data.payload.authCode) this.charge.authCode = data.payload.authCode;

    this.router.navigate(ChargeManagementEditViewModel, undefined, {
      countryId: this.charge.vehicle.countryId,
      licensePlate: this.charge.vehicle.licensePlate,
      authCode: this.charge.authCode,
    });
  }
}
